/**
 * CameraManager
 * Manages camera access, video stream, and camera switching
 * 
 * Requirements: 1.1, 1.2, 1.4, 1.5
 */

import { CameraFacingMode } from '../types';

export class CameraManager {
  private stream: MediaStream | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private currentFacingMode: CameraFacingMode = 'environment';
  private orientationChangeHandler: (() => void) | null = null;

  /**
   * Detect if device is mobile
   */
  private isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }

  /**
   * Get optimal camera constraints based on device type
   * Requirements: 10.2
   */
  private getCameraConstraints(facingMode: CameraFacingMode): MediaStreamConstraints {
    const isMobile = this.isMobileDevice();

    if (isMobile) {
      // Mobile-optimized constraints
      // Use lower resolution for better performance on mobile
      return {
        video: {
          facingMode: { ideal: facingMode },
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 },
          aspectRatio: { ideal: 16 / 9 },
          frameRate: { ideal: 30, max: 30 },
        },
        audio: false,
      };
    } else {
      // Desktop constraints
      return {
        video: {
          facingMode: { ideal: facingMode },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          aspectRatio: { ideal: 16 / 9 },
        },
        audio: false,
      };
    }
  }

  /**
   * Request camera access and get video stream
   * Requirements: 1.1, 1.2, 10.2
   */
  async requestCameraAccess(facingMode: CameraFacingMode = 'environment'): Promise<MediaStream> {
    try {
      // Check if MediaDevices API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera API not supported in this browser');
      }

      // iOS-specific: Check if we're in a secure context
      if (!window.isSecureContext) {
        throw new Error('Camera access requires HTTPS. Please use a secure connection.');
      }

      // Get appropriate constraints for device
      const constraints = this.getCameraConstraints(facingMode);

      // iOS-specific: Add additional constraints for better compatibility
      if (this.isMobileDevice() && /iPhone|iPad/.test(navigator.userAgent)) {
        console.log('[CameraManager] Applying iOS-specific camera constraints');
        // iOS Safari sometimes needs exact constraints instead of ideal
        if (constraints.video && typeof constraints.video === 'object') {
          constraints.video = {
            ...constraints.video,
            // Use exact for iOS to avoid permission issues
            facingMode: facingMode === 'environment' ? { exact: 'environment' } : { exact: 'user' },
            // Lower resolution for iOS stability
            width: { ideal: 1280, max: 1280 },
            height: { ideal: 720, max: 720 },
          };
        }
      }

      console.log('[CameraManager] Requesting camera with constraints:', constraints);
      this.stream = await navigator.mediaDevices.getUserMedia(constraints);
      this.currentFacingMode = facingMode;

      // Setup orientation change handling for mobile
      this.setupOrientationHandling();

      console.log('[CameraManager] Camera access granted successfully');
      return this.stream;
    } catch (error) {
      console.error('[CameraManager] Camera access failed:', error);
      
      // Handle permission denied errors
      // Requirement: 1.5
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          // iOS-specific guidance
          if (/iPhone|iPad/.test(navigator.userAgent)) {
            throw new Error(
              'Camera permission denied. On iOS: Go to Settings > Safari > Camera, or Settings > Chrome > Camera and enable access. Then refresh this page.'
            );
          } else {
            throw new Error(
              'Camera permission denied. Please enable camera access in your browser settings and refresh the page.'
            );
          }
        } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
          throw new Error(
            'No camera found. Please check that your device has a camera connected.'
          );
        } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
          throw new Error(
            'Camera is already in use by another application. Please close other apps using the camera.'
          );
        } else if (error.name === 'OverconstrainedError') {
          // Try fallback constraints for iOS
          if (this.isMobileDevice() && facingMode === 'environment') {
            console.log('[CameraManager] Retrying with fallback constraints');
            try {
              const fallbackConstraints = {
                video: {
                  facingMode: 'environment', // Remove exact constraint
                  width: { ideal: 640 },
                  height: { ideal: 480 },
                },
                audio: false,
              };
              this.stream = await navigator.mediaDevices.getUserMedia(fallbackConstraints);
              this.currentFacingMode = facingMode;
              this.setupOrientationHandling();
              return this.stream;
            } catch (fallbackError) {
              throw new Error('Camera constraints not supported. Try using the front camera.');
            }
          }
          throw new Error('Camera constraints not supported. Try using a different camera.');
        } else {
          throw new Error(`Camera access failed: ${error.message}`);
        }
      }
      throw error;
    }
  }

  /**
   * Switch between front and rear cameras
   * Requirement: 1.4
   */
  async switchCamera(facingMode: CameraFacingMode): Promise<void> {
    // Stop current stream
    this.release();

    // Request new stream with different facing mode
    const newStream = await this.requestCameraAccess(facingMode);

    // Update video element if it exists
    if (this.videoElement) {
      this.videoElement.srcObject = newStream;
    }
  }

  /**
   * Get or create video element for displaying stream
   */
  getVideoElement(): HTMLVideoElement {
    if (!this.videoElement) {
      this.videoElement = document.createElement('video');
      this.videoElement.autoplay = true;
      this.videoElement.playsInline = true; // Important for iOS
      this.videoElement.muted = true;
    }

    if (this.stream && this.videoElement.srcObject !== this.stream) {
      this.videoElement.srcObject = this.stream;
    }

    return this.videoElement;
  }

  /**
   * Set video element to use for stream display
   */
  setVideoElement(element: HTMLVideoElement): void {
    this.videoElement = element;
    if (this.stream) {
      this.videoElement.srcObject = this.stream;
    }
  }

  /**
   * Get current video stream
   */
  getStream(): MediaStream | null {
    return this.stream;
  }

  /**
   * Get current facing mode
   */
  getCurrentFacingMode(): CameraFacingMode {
    return this.currentFacingMode;
  }

  /**
   * Check if camera is active
   */
  isActive(): boolean {
    return this.stream !== null && this.stream.active;
  }

  /**
   * Setup orientation change handling for mobile devices
   * Requirements: 10.2
   */
  private setupOrientationHandling(): void {
    if (!this.isMobileDevice()) {
      return;
    }

    // Remove existing handler if any
    this.removeOrientationHandling();

    // Create new handler
    this.orientationChangeHandler = () => {
      // Handle orientation change
      // The video element will automatically adjust, but we log for debugging
      console.log('[Camera] Device orientation changed');
      
      // Optionally adjust video element styling
      if (this.videoElement) {
        // Force video to recalculate dimensions
        this.videoElement.style.transform = 'none';
        setTimeout(() => {
          if (this.videoElement) {
            this.videoElement.style.transform = '';
          }
        }, 100);
      }
    };

    // Listen for orientation changes
    if (window.screen && 'orientation' in window.screen) {
      window.screen.orientation.addEventListener('change', this.orientationChangeHandler);
    } else {
      // Fallback for older browsers
      window.addEventListener('orientationchange', this.orientationChangeHandler);
    }
  }

  /**
   * Remove orientation change handling
   */
  private removeOrientationHandling(): void {
    if (!this.orientationChangeHandler) {
      return;
    }

    if (window.screen && 'orientation' in window.screen) {
      window.screen.orientation.removeEventListener('change', this.orientationChangeHandler);
    } else {
      window.removeEventListener('orientationchange', this.orientationChangeHandler);
    }

    this.orientationChangeHandler = null;
  }

  /**
   * Release camera resources
   */
  release(): void {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }

    if (this.videoElement) {
      this.videoElement.srcObject = null;
    }

    // Remove orientation handling
    this.removeOrientationHandling();
  }

  /**
   * Get available camera devices
   */
  async getAvailableCameras(): Promise<MediaDeviceInfo[]> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.filter(device => device.kind === 'videoinput');
    } catch (error) {
      console.error('Failed to enumerate devices:', error);
      return [];
    }
  }

  /**
   * Check if device has multiple cameras
   */
  async hasMultipleCameras(): Promise<boolean> {
    const cameras = await this.getAvailableCameras();
    return cameras.length > 1;
  }
}

export default CameraManager;
